import React, { useState, useEffect } from 'react';
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

// Modals
import { CaseInfo } from './modals/caseInfo';
import { DynamicTooltip, Pill, IconButton, Icons } from '@gpstrackit/gtc-react-controls';
import { Timezone } from 'Components/uiControls';

// Views
import AdvancedFilters from './components/advancedFilters';
import DashboardView from '../../components/dashboard';
import Preferences from "Core/containers/Preferences";
import DriverInfo from '../events/modals/driverInfo';
import TagInfo from '../events/modals/tagInfo';
import CaseModalInfo from '../events/modals/caseInfo';

// Helpers
import { HasPermission } from "Core/security";
import { getInitialRange } from '../../.globals/helpers';

// Redux Actions
import { savePreference, setDriverPerformanceMainTab } from 'Redux/actions';
import { openModalAdvancedFilters } from './components/advancedFilters/redux/slice';
import { driverPerformanceDashboardUpdateFilters, driverPerformanceDashboardUpdatSorts } from './redux/slice';

// Styles
import './index.scss';

// Initial State for each gadget
const INITIAL_STATE = {
    rangeDate: {
        start: moment.utc().subtract(7, 'days').startOf('day').toDate(),
        end: moment.utc().subtract(1, 'days').endOf('day').toDate()
    },
    timeFrame: 'last7days',
    tags: [],
    events: [],
    drivers: [],
    videoEvents: [],
    isUnassignedDriver: false,
    tagHierarchyRootId: null,
    tagLevel: null
};

const KEY_PREFERENCE = "DrpDasboardTab";

// Helper function to extract values from array of objects or strings
const extractFilterValues = (filterArray, possibleKeys = ['id', 'value', 'name']) => {
    if (!Array.isArray(filterArray) || filterArray.length === 0) {
        return [];
    }

    return filterArray
        .map(item => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item !== null) {
                // Try to find a valid property in the object
                for (const key of possibleKeys) {
                    if (item[key]) return item[key];
                }
            }
            return null;
        })
        .filter(Boolean);
};

// Helper function to render column content based on type
const renderColumnByType = (row, column, user, dispatch, currentFilters, navigate, gadgetkey, messages) => {
    const { type = 'normal', value = '' } = column;
    let cellValue = row[value];
    const { dateformat, timeZone } = user || {};

    if (!type && !cellValue) {
        return <span></span>
    }

    if (value === "statusLabel") {
        cellValue = messages[`driver_performance_cases_status_${cellValue?.toLowerCase()}`] || cellValue
    }

    if(value === "nextStep"){
        cellValue = messages[`driver_performance_cases_step_${cellValue.replace(/\s/g, "")}`] || cellValue
    }

    switch (type) {
        case 'date':
            return cellValue && (
                <Timezone
                    date={cellValue}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            );
        case 'pill':
            return (
                <Pill

                    size="dinamic"
                    item={{ id: cellValue, label: cellValue, color: `red` }}
                />
            );
        case 'linkCase':
            return (
                <div>
                    <DynamicTooltip
                        trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{row?.caseNumber || "N/A"}</span>}
                        openOn="click"
                        size="medium"
                        position="top"
                    >
                        {(isOpen) => (
                            <CaseInfo
                                caseNumber={row?.caseNumber}
                                openTooltip={isOpen}
                            />
                        )}
                    </DynamicTooltip>
                </div>
            );
        case 'pillStatus':
            const statusLabel = typeof cellValue === 'object' && cellValue !== null
                ? (cellValue.label || cellValue.entityId || '')
                : cellValue;
            return (
                <div style={{ width: 100 }} className="dashboard-badge-status">
                    <Pill
                        size="dinamic"
                        item={{
                            id: statusLabel,
                            label: messages[`pending_coach_case_status_${statusLabel.toLowerCase()}`] || statusLabel,
                            color: cellValue?.label === 'Pending' ? "grey" : cellValue?.label === 'Evaluate' ? "orange" : "#66bb6a"
                        }}
                        variant={'outlined'}
                        style={{ backgroundColor: `green !important` }}
                    />
                </div>
            );
        case 'objectLabel':
            const value = column.value.split('.');
            const numValues = value.length;
            let valueToShow = row;
            for (let i = 0; i < numValues; i++) {
                valueToShow = valueToShow?.[value[i]];
            }
            return (
                <div>
                    <span>
                        {messages[`pending_coach_case_next_step_${valueToShow?.toString().trim().toLowerCase().replaceAll(" ", "_")}`] ?? valueToShow}
                    </span>
                </div>
            );
        case 'objectLabelPercent':
            return (
                <div>
                    <span>{cellValue || 0}%</span>
                </div>
            );
        case 'link':
            const buildLinkUrl = () => {
                if (cellValue == "0") {
                    return null;
                }

                const params = new URLSearchParams();

                // Use 'events' prefix for all parameters to avoid conflicts with other tabs
                if (row.driverId && gadgetkey === 'RiskEventsByDriver') {
                    params.append('eventsDriverId', row.driverId);
                }
                if (row.tagId && gadgetkey === 'RiskEventsByTag') {
                    params.append('eventsTagId', row.tagId);
                }

                if (currentFilters?.timeFrame) {
                    if (currentFilters?.timeFrame !== 'custom') {
                        params.append('eventsRange', currentFilters.timeFrame);
                    } else if (currentFilters.rangeDate) {
                        params.append('eventsStartDate', currentFilters.rangeDate?.start);
                        params.append('eventsEndDate', currentFilters.rangeDate?.end);
                    }
                }

                const filterMappings = [
                    { key: 'eventsNames', data: currentFilters?.events, possibleKeys: ['id', 'value', 'name', 'eventName', 'label'] },
                ];

                filterMappings.forEach(({ key, data, possibleKeys }) => {
                    const values = extractFilterValues(data, possibleKeys);
                    if (values.length > 0) {
                        params.append(key, values.join(','));
                    }
                });

                const queryString = params.toString();
                return queryString ? `/app/driverPerformance/main?${queryString}` : '/app/driverPerformance/main';
            };

            const linkUrl = buildLinkUrl();

            return (
                <a
                    href={linkUrl || '#'}
                    onClick={(e) => {
                        e.preventDefault();

                        if (cellValue == "0") {
                            return;
                        }

                        dispatch(setDriverPerformanceMainTab(2));
                        navigate(linkUrl);
                    }}
                    style={{
                        color: (cellValue != "0") ? '#005bf8' : "black",
                        textDecoration: (cellValue != "0") ? 'underline' : 'none',
                        cursor: (cellValue != "0") ? 'pointer' : 'auto'
                    }}
                >
                    {cellValue}
                </a>
            );
        case 'dynamicTooltip':
            if (column?.key === "driverName") {
                return (
                    <DynamicTooltip
                        trigger={<div style={{ wordWrap: 'break-word', width: 200 }}><span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{cellValue}</span></div>}
                        openOn="click"
                        size="medium"
                        position="top"
                    >
                        {(isOpen) => (
                            <DriverInfo
                                driverKey={row.driverId}
                                openTooltip={isOpen}
                                driverId={row?.driverId}
                            />
                        )}
                    </DynamicTooltip>
                );
            } else if (column?.key === "coachName") {
                return (
                    <div style={{ wordWrap: 'break-word', width: 200 }}><span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{cellValue}</span></div>
                );
            } else if (column?.key === "caseNumber") {
                return (
                    <DynamicTooltip
                        trigger={<div style={{ wordWrap: 'break-word', width: 200 }}><span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{cellValue}</span></div>}
                        openOn="click"
                        size="medium"
                        position="top"
                    >
                        {(isOpen) => (
                            <CaseModalInfo
                                caseNumber={cellValue}
                                openTooltip={isOpen}
                            />
                        )}
                    </DynamicTooltip>
                );
            } else {
                return (
                    <DynamicTooltip
                        trigger={<div style={{ wordWrap: 'break-word', width: 200 }}><span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{cellValue}</span></div>}
                        openOn="click"
                        size="medium"
                        position="top"
                    >
                        {(isOpen) => (
                            <TagInfo
                                openTooltip={isOpen}
                                row={row}
                            />
                        )}
                    </DynamicTooltip>
                );
            }

        case 'normal':
        default:
            return <span>{cellValue}</span>;
    }
}

const onClickSummaryGadget = (gadgetkey, value, dispatch, navigate) => {
    const { label, filters, } = value || {};
    const params = new URLSearchParams();
    const aggregatedParams = new Map();

    const addAggregatedValues = (key, values = []) => {
        if (!Array.isArray(values) || !values.length) return;

        const existingValues = aggregatedParams.get(key) || [];
        values.forEach(rawValue => {
            if (rawValue === undefined || rawValue === null || rawValue === '') return;
            const normalizedValue = String(rawValue);
            if (!existingValues.includes(normalizedValue)) {
                existingValues.push(normalizedValue);
            }
        });
        aggregatedParams.set(key, existingValues);
    };

    // Use 'events' prefix for all parameters to avoid conflicts with other tabs
    if (label) {
        addAggregatedValues('eventsNames', [label]);
    }

    if (filters?.driverId && gadgetkey === 'RiskEventsByDriver') {
        params.append('eventsDriverId', filters.driverId);
    }

    if (filters?.timeFrame) {
        if (filters?.timeFrame !== 'custom') {
            params.append('eventsRange', filters.timeFrame);
        } else if (filters.rangeDate) {
            params.append('eventsStartDate', filters.rangeDate?.start);
            params.append('eventsEndDate', filters.rangeDate?.end);
        }
    }

    // Add filters to URL dynamically
    const filterMappings = [
        ...(label ? [] : [{ key: 'eventsNames', data: filters?.events, possibleKeys: ['id', 'value', 'name', 'eventName', 'label'] }]),
        { key: 'eventsTags', data: filters?.tags, possibleKeys: ['id', 'value'] },
    ];

    filterMappings.forEach(({ key, data, possibleKeys }) => {
        const values = extractFilterValues(data, possibleKeys);
        addAggregatedValues(key, values);
    });

    // Navigate with parameters
    aggregatedParams.forEach((values, key) => {
        if (values.length > 0) {
            params.append(key, values.join(','));
        }
    });
    const queryString = params.toString();

    dispatch(setDriverPerformanceMainTab(2));

    if (queryString) {
        navigate(`/app/driverPerformance/main?${queryString}`);
    } else {
        navigate('/app/driverPerformance/main');
    }
}

const DriverPerformanceDashboardView = () => {
    //Language
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux States
    const { gadgetId } = useSelector(state => state.driverPerformanceDashboardTabAdvancedFiltersRedux);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const { filters, sorts } = useSelector(state => state.driverPerformanceDashboardRedux);
    const user = useSelector(state => state.securityRedux.user);

    // Local States for each gadget's formData
    const [loadPreference, setLoadPreferences] = useState(false);
    const [preferences, setPreferences] = useState({});
    // Function to get differences between two objects (used for filters comparison)
    const getDifferences = (base, updated, exclude = []) => {
        // Ensure we're working with plain objects
        const cleanBase = base ? JSON.parse(JSON.stringify(base)) : {};
        const cleanUpdated = updated ? JSON.parse(JSON.stringify(updated)) : {};

        function isEqual(a, b) {
            if (a === b) return true;
            if (Array.isArray(a) && Array.isArray(b)) {
                if (a.length !== b.length) return false;
                return a.every((val, i) => isEqual(val, b[i]));
            }
            if (typeof a === "object" && a && typeof b === "object" && b) {
                const keysA = Object.keys(a), keysB = Object.keys(b);
                if (keysA.length !== keysB.length) return false;
                return keysA.every(k => isEqual(a[k], b[k]));
            }
            return false;
        }

        function diff(a, b) {
            if (isEqual(a, b)) return {};
            if (Array.isArray(a) && Array.isArray(b)) return [...b];
            if (a == null || b == null || typeof a !== "object" || typeof b !== "object") return b;

            const result = {};
            for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
                if (exclude.includes(key)) continue;
                const d = diff(a[key], b[key]);
                if (d == null || typeof d !== "object" || Array.isArray(d) || Object.keys(d).length) {
                    result[key] = d;
                }
            }
            return result;
        }

        return diff(cleanBase, cleanUpdated);
    }

    // Function to apply changes to current gadget
    const applyChanges = (gadgetKey, data) => {
        // Create a clean copy of current filters
        let currentFilters = JSON.parse(JSON.stringify(filters));

        if (gadgetKey === 'globals') {
            // If applying to globals, update global filters with clean data
            currentFilters.globals = JSON.parse(JSON.stringify(data));

            // IMPORTANT: When globals are modified, remove all gadget-specific filters
            // so that ALL gadgets inherit the new global values
            Object.keys(currentFilters).forEach(key => {
                if (key !== 'globals') {
                    // Preserve videoEvents for EventHighlights when clearing globals
                    if (key === 'EventHighlights' && currentFilters[key]?.videoEvents) {
                        currentFilters[key] = { videoEvents: currentFilters[key].videoEvents, includeUnassignedDriver: currentFilters[key]?.includeUnassignedDriver || false };
                    } else {
                        delete currentFilters[key];
                    }
                }
            });

        } else {
            // If applying to specific gadget, calculate differences from globals
            const globalFilters = currentFilters.globals || {};

            // Special handling for EventHighlights gadget
            if (gadgetKey === 'EventHighlights') {
                // For EventHighlights, we store the videoEvents directly without calculating differences
                // because videoEvents is a gadget-specific filter that doesn't exist in globals
                currentFilters[gadgetKey] = {
                    videoEvents: data.videoEvents || [],
                    includeUnassignedDriver: data.includeUnassignedDriver || false
                };
            } else {
                // For other gadgets, calculate differences from globals
                const differences = getDifferences(globalFilters, data, /* ['rangeDate'] */);

                // Always store the differences for the gadget (even if it results in blank filters)
                // This ensures the gadget has its own specific state
                currentFilters[gadgetKey] = differences;
            }
        }

        // Update Redux
        dispatch(driverPerformanceDashboardUpdateFilters(currentFilters));
        // Save to Preferences
        savePreferences([{ key: "filters", value: currentFilters }]);
    };

    // Function to save preferences
    const savePreferences = (preferencesArray) => {
        if (!Array.isArray(preferencesArray)) {
            console.error("Expected an array of preferences");
            return;
        }

        let updatedPreferences = { ...preferences } || {};

        preferencesArray.forEach(({ key, value }) => {
            updatedPreferences[key] = value;
        });

        setPreferences(updatedPreferences);

        dispatch(savePreference({
            entityName: "User",
            entityId: authenticatedId,
            preferenceKey: KEY_PREFERENCE,
            preferenceValue: updatedPreferences
        }));
    };

    // Function to handle configuration (open Advanced Filters modal)
    const configHandler = (key) => {
        dispatch(openModalAdvancedFilters({ open: true, gadgetId: key }));
    }

    // Function to handle hierarchy changes from the gadget popover
    const onHierarchyChange = (gadgetKey, hierarchyData) => {
        const currentGadgetFilters = getCurrentFilters(gadgetKey) || {};
        const updatedFilters = {
            ...currentGadgetFilters,
            tagHierarchyRootId: hierarchyData?.tagHierarchyRootId ?? null,
            tagLevel: hierarchyData?.tagLevel ?? null
        };
        applyChanges(gadgetKey, updatedFilters);
    };

    // Function to deeply merge two objects (used for merging filters)
    const mergeObjects = (base, updated) => {
        if (!base) return updated ? JSON.parse(JSON.stringify(updated)) : {};
        if (!updated) return base ? JSON.parse(JSON.stringify(base)) : {};

        // Ensure we're working with plain objects
        const cleanBase = JSON.parse(JSON.stringify(base));
        const cleanUpdated = JSON.parse(JSON.stringify(updated));

        // For arrays, use the updated array if it exists, otherwise use base
        if (Array.isArray(cleanBase) && Array.isArray(cleanUpdated)) {
            return [...cleanUpdated];
        }

        if (Array.isArray(cleanBase) || Array.isArray(cleanUpdated)) {
            return cleanUpdated || cleanBase;
        }

        if (typeof cleanBase !== "object" || cleanBase === null) return cleanUpdated;
        if (typeof cleanUpdated !== "object" || cleanUpdated === null) return cleanBase;

        const result = { ...cleanBase };
        for (const key of Object.keys(cleanUpdated)) {
            if (cleanUpdated[key] !== undefined && cleanUpdated[key] !== null) {
                if (typeof cleanBase[key] === "object" && typeof cleanUpdated[key] === "object" &&
                    !Array.isArray(cleanBase[key]) && !Array.isArray(cleanUpdated[key])) {
                    result[key] = mergeObjects(cleanBase[key], cleanUpdated[key]);
                } else {
                    result[key] = cleanUpdated[key];
                }
            }
        }
        return result;
    };

    const getCurrentFilters = (specificGadgetId = null, gadgetSettings = null) => {
        const globalFilters = filters?.globals || {};

        // If requesting global filters specifically, return only global filters
        if (specificGadgetId === 'globals') {
            return globalFilters;
        }

        // If no specific gadget ID provided, return global filters
        if (!specificGadgetId) {
            return globalFilters;
        }

        // For specific gadgets, merge global with gadget-specific filters
        const gadgetSpecificFilters = filters?.[specificGadgetId] || {};
        const mergedFilters = mergeObjects(globalFilters, gadgetSpecificFilters);

        // If the gadget has a defaultTimeFrame in its settings (from DB), apply it
        const defaultTimeFrame = gadgetSettings?.defaultTimeFrame;
        if (defaultTimeFrame?.type === 'relative' && defaultTimeFrame?.days) {
            const daysToTimeFrameKey = {
                7: 'last7days',
                15: 'last15days',
                30: 'last30Days',
                90: 'last90days'
            };
            const timeFrameKey = daysToTimeFrameKey[defaultTimeFrame.days];
            if (timeFrameKey) {
                const freshRange = getInitialRange(timeFrameKey);
                mergedFilters.timeFrame = timeFrameKey;
                mergedFilters.rangeDate = {
                    start: freshRange.start,
                    end: freshRange.end
                };
            }
        } else {
            // No defaultTimeFrame in DB — do not send date filter to API
            mergedFilters.timeFrame = null;
            mergedFilters.rangeDate = null;
        }

        // Apply tag hierarchy settings from gadget config as defaults
        // User-selected filters (stored in Redux) take precedence
        if (gadgetSettings?.tagHierarchyRootId && !mergedFilters.tagHierarchyRootId) {
            mergedFilters.tagHierarchyRootId = String(gadgetSettings.tagHierarchyRootId);
        }
        if (gadgetSettings?.tagLevel !== undefined && gadgetSettings?.tagLevel !== null && mergedFilters.tagLevel === null) {
            mergedFilters.tagLevel = String(gadgetSettings.tagLevel);
        }

        return mergedFilters;
    }

    const hasFilters = (gadgetKey) => {
        if (gadgetKey === 'initial' || gadgetKey === 'globals') {
            // Check if global filters differ from initial state
            const globalFilters = filters?.globals || {};
            const excludedKeys = ["rangeDate", "timeFrame"];
            const defaultValues = INITIAL_STATE;

            return Object.keys(globalFilters).some(key => {
                if (excludedKeys.includes(key)) {
                    return false;
                }

                const currentValue = globalFilters[key];
                const defaultValue = defaultValues[key];

                if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
                    return currentValue.length !== defaultValue.length ||
                        currentValue.some((item, index) => item !== defaultValue[index]);
                }
                return currentValue !== defaultValue;
            });
        }

        // For specific gadgets, check if they have custom filters (different from globals)
        const gadgetSpecificFilters = filters?.[gadgetKey];

        // More robust check - ensure the key exists and has meaningful content
        const hasCustomFilters = Boolean(
            gadgetSpecificFilters &&
            typeof gadgetSpecificFilters === 'object' &&
            !Array.isArray(gadgetSpecificFilters) &&
            Object.keys(gadgetSpecificFilters).length > 0
        );

        return hasCustomFilters;
    };

    return (
        <div style={{ width: '100%' }}>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        if (data?.filters) {
                            // Recalculate rangeDate from the saved timeFrame so dates are always fresh
                            const savedFilters = JSON.parse(JSON.stringify(data.filters));
                            if (savedFilters.globals) {
                                const tf = savedFilters.globals.timeFrame || 'last7days';
                                if (tf !== 'custom') {
                                    const freshRange = getInitialRange(tf);
                                    savedFilters.globals.rangeDate = {
                                        start: freshRange.start,
                                        end: freshRange.end
                                    };
                                    savedFilters.globals.timeFrame = tf;
                                }
                            }
                            dispatch(driverPerformanceDashboardUpdateFilters(savedFilters));
                        }
                        let defaultSorts = []
                        if (data?.RiskEventsByDriver && data?.RiskEventsByDriver?.type === "sort") {
                            defaultSorts.push({ key: "RiskEventsByDriver", value: data?.RiskEventsByDriver?.value })
                        }
                        if (data?.RiskEventsByTag && data?.RiskEventsByDriver?.type === "sort") {
                            defaultSorts.push({ key: "RiskEventsByTag", value: data?.RiskEventsByTag?.value })
                        }
                        if (defaultSorts.length) {
                            dispatch(driverPerformanceDashboardUpdatSorts(defaultSorts))
                        }
                        setPreferences(data)
                        setLoadPreferences(true)
                    }
                }}
            >
                <div style={{ padding: '0 10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {
                        HasPermission(user, 'drpconf', [326]) &&
                        <IconButton
                            tooltip={messages['menu_settings']}
                            variant="secondary"
                            icon="settings"
                            placement="bottom"
                            onClick={() => {
                                navigate(`/app/driverPerformance/configurationDetail`);
                            }}
                        />
                    }
                </div>
                <DashboardView
                    location='driverPerformance'
                    configHandler={configHandler}
                    filters={filters}
                    getCurrentFilters={getCurrentFilters}
                    hasFilters={hasFilters}
                    renderColumnByType={renderColumnByType}
                    onClick={onClickSummaryGadget}
                    savePreferences={savePreferences}
                    defaultSorts={sorts}
                    onHierarchyChange={onHierarchyChange}
                />
            </Preferences>
            <AdvancedFilters
                formData={getCurrentFilters(gadgetId || 'globals')}
                applyChanges={applyChanges}
                hasFilters={() => hasFilters(gadgetId || 'globals')}
                loading={false}
            />
        </div>
    );
};

export default DriverPerformanceDashboardView;
